import { Popover } from "@base-ui-components/react/popover"
import cx from "classnames"
import { AlertCircle, Archive, ChevronDown, FileText, ImagePlus, Loader2, RotateCcw, X } from "lucide-react"
import { observer } from "mobx-react"
import { type KeyboardEvent as ReactKeyboardEvent, useEffect, useMemo, useRef, useState } from "react"
import { useHotkeys } from "react-hotkeys-hook"
import { FastModeToggle } from "../components/FastModeToggle"
import { ImageDropOverlay } from "../components/ImageDropOverlay"
import { SmartEditor, type SmartEditorRef } from "../components/SmartEditor"
import { TaskCreateAgentActions } from "../components/TaskCreateAgentActions"
import { AdminCapabilitiesSwitch } from "../components/AdminCapabilitiesSwitch"
import { ThinkingPicker } from "../components/ThinkingPicker"
import { INPUT_FOCUS_HOTKEYS, MAIN_INPUT_HOTKEY_SCOPE_PROPS } from "../components/inputHotkeyScope"
import { TaskMcpSelector } from "../components/mcp/TaskMcpSelector"
import { useImageDropZone } from "../hooks/useImageDropZone"
import { usePortalContainer } from "../hooks/usePortalContainer"
import { providerModelRegistry } from "../modelRegistry"
import { agentTargetId } from "../agentTarget"
import { getNewTaskAgentOptions, getNewTaskPinnedAgents, type NewTaskAgentOption } from "../newTaskAgentOptions"
import { useCodeNavigate } from "../routing"
import { useCodeStore } from "../store/context"
import { SdkCapabilitiesManager } from "../store/managers/SdkCapabilitiesManager"
import type { StashedDraft } from "../store/managers/SmartEditorManager"
import type { TaskCreation } from "../store/managers/TaskCreationManager"
import type { Repo } from "../types"
import { processImageBlob } from "../utils/imageAttachment"
import { getTaskCreationPhaseLabel } from "./taskCreationPhaseLabel"

interface TaskCreatePageProps {
    workspaceId: string
    repo: Repo
}

function isSubmitShortcut(event: ReactKeyboardEvent): boolean {
    return event.key === "Enter" && (event.metaKey || event.ctrlKey) && !event.shiftKey && !event.altKey
}

function PendingTaskItem({
    creation,
    onClick,
    onRetry,
    onDismiss,
}: {
    creation: TaskCreation
    onClick: () => void
    onRetry: () => void
    onDismiss: () => void
}) {
    const hasError = creation.error !== null

    return (
        <div
            role="button"
            tabIndex={0}
            onClick={onClick}
            onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault()
                    onClick()
                }
            }}
            className={cx("p-3 border-b border-border cursor-pointer transition-colors", hasError ? "bg-error/5 hover:bg-error/10" : "hover:bg-base-200")}
        >
            <div className="flex items-start gap-3">
                <div className="flex-shrink-0 mt-0.5">
                    {hasError ? <AlertCircle size="1rem" className="text-error" /> : <Loader2 size="1rem" className="animate-spin text-primary" />}
                </div>
                <div className="flex-1 min-w-0">
                    <p className={cx("text-sm truncate", hasError ? "text-error" : "text-base-content")}>
                        {creation.description.length > 60 ? `${creation.description.slice(0, 60)}...` : creation.description}
                    </p>
                    <div className="flex items-center gap-2 mt-1">
                        <span className={cx("text-xs", hasError ? "text-error/70" : "text-muted")}>
                            {hasError ? "Failed" : getTaskCreationPhaseLabel(creation.phase)}
                        </span>
                    </div>
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                    {hasError && (
                        <button
                            type="button"
                            onClick={(e) => {
                                e.stopPropagation()
                                onRetry()
                            }}
                            className="btn p-1.5 text-muted hover:text-base-content hover:bg-base-300 transition-colors"
                            title="Retry"
                        >
                            <RotateCcw size="0.875rem" />
                        </button>
                    )}
                    <button
                        type="button"
                        onClick={(e) => {
                            e.stopPropagation()
                            onDismiss()
                        }}
                        className="btn p-1.5 text-muted hover:text-base-content hover:bg-base-300 transition-colors"
                        title="Dismiss"
                    >
                        <X size="0.875rem" />
                    </button>
                </div>
            </div>
        </div>
    )
}

function formatDraftPreview(draft: StashedDraft): string {
    const imageCount = draft.snapshot.pendingImages.length
    const normalized = draft.snapshot.value.replace(/\s+/g, " ").trim()
    if (normalized.length > 0) {
        return normalized.length > 96 ? `${normalized.slice(0, 96)}...` : normalized
    }
    if (imageCount > 0) {
        return `Draft with ${imageCount} image${imageCount === 1 ? "" : "s"}`
    }
    return "Empty draft"
}

function formatDraftTime(createdAt: string): string {
    return new Intl.DateTimeFormat(undefined, {
        hour: "numeric",
        minute: "2-digit",
    }).format(new Date(createdAt))
}

function StashedDraftItem({ draft, onPop, onDelete }: { draft: StashedDraft; onPop: () => void; onDelete: () => void }) {
    const imageCount = draft.snapshot.pendingImages.length
    const preview = formatDraftPreview(draft)

    return (
        <div className="border border-border bg-base-100 p-3">
            <div className="flex items-start gap-2">
                <FileText size={14} className="mt-0.5 flex-shrink-0 text-muted" />
                <div className="min-w-0 flex-1">
                    <p className="text-sm text-base-content break-words" title={preview}>
                        {preview}
                    </p>
                    <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-muted">
                        <span>{formatDraftTime(draft.createdAt)}</span>
                        {imageCount > 0 && (
                            <span>
                                {imageCount} image{imageCount === 1 ? "" : "s"}
                            </span>
                        )}
                    </div>
                </div>
            </div>
            <div className="mt-3 flex items-center gap-2">
                <button
                    type="button"
                    onClick={onPop}
                    className="btn flex items-center gap-1.5 px-2.5 py-1.5 text-xs bg-base-200 text-base-content hover:bg-base-300 transition-colors"
                >
                    <RotateCcw size={12} />
                    Pop
                </button>
                <button
                    type="button"
                    onClick={onDelete}
                    className="btn flex items-center gap-1.5 px-2.5 py-1.5 text-xs text-muted hover:text-base-content hover:bg-base-200 transition-colors"
                >
                    <X size={12} />
                    Delete
                </button>
            </div>
        </div>
    )
}

export const TaskCreateDraftsMenu = observer(({ workspaceId, mobileAdvanced = false }: { workspaceId: string; mobileAdvanced?: boolean }) => {
    const codeStore = useCodeStore()
    const portalContainer = usePortalContainer()
    const editorManager = codeStore.smartEditors.getManager("task-create", workspaceId)

    const handleStash = () => {
        editorManager.stashCurrentDraft()
    }

    return (
        <div className={cx("flex items-center gap-2", mobileAdvanced ? "" : "max-[900px]:hidden")}>
            <button
                type="button"
                onClick={handleStash}
                disabled={!editorManager.hasDraftableContent}
                className={cx(
                    "btn flex items-center gap-1.5 px-3 text-sm transition-colors",
                    mobileAdvanced ? "min-h-11 py-2" : "py-1.5",
                    editorManager.hasDraftableContent
                        ? "bg-base-200 text-base-content hover:bg-base-300 cursor-pointer"
                        : "bg-base-200/40 text-base-content/50 cursor-not-allowed"
                )}
            >
                <Archive size={14} />
                <span>Stash</span>
            </button>

            <Popover.Root>
                <Popover.Trigger
                    className={cx(
                        "btn flex items-center gap-1.5 px-2 text-sm text-muted hover:text-base-content hover:bg-base-200 transition-colors",
                        mobileAdvanced ? "min-h-11 py-2" : "py-1.5"
                    )}
                >
                    <Archive size={14} />
                    <span>Drafts</span>
                    {editorManager.stashedDrafts.length > 0 && (
                        <span className="min-w-5 px-1.5 py-0.5 text-xs bg-base-200 text-base-content">{editorManager.stashedDrafts.length}</span>
                    )}
                    <ChevronDown size={12} />
                </Popover.Trigger>
                <Popover.Portal container={portalContainer}>
                    <Popover.Positioner sideOffset={8} side={mobileAdvanced ? "top" : "bottom"} align="end">
                        <Popover.Popup className="z-50 w-[min(20rem,calc(100vw-1rem))] bg-base-100 border border-border shadow-lg outline-none">
                            <div className="flex items-center justify-between border-b border-border px-3 py-2">
                                <div className="flex items-center gap-2 text-sm text-base-content">
                                    <Archive size={14} className="text-muted" />
                                    <span>Drafts</span>
                                </div>
                                <span className="text-xs text-muted">{editorManager.stashedDrafts.length}</span>
                            </div>
                            <div className="flex flex-col gap-3 p-3">
                                <button
                                    type="button"
                                    onClick={handleStash}
                                    disabled={!editorManager.hasDraftableContent}
                                    className={cx(
                                        "btn flex w-full items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium transition-all",
                                        editorManager.hasDraftableContent
                                            ? "bg-base-200 text-base-content hover:bg-base-300 cursor-pointer"
                                            : "bg-base-200/40 text-base-content/50 cursor-not-allowed"
                                    )}
                                >
                                    <Archive size={14} />
                                    Stash current draft
                                </button>
                                {editorManager.stashedDrafts.length > 0 ? (
                                    <div className="flex max-h-80 flex-col gap-3 overflow-y-auto">
                                        {editorManager.stashedDrafts.map((draft) => (
                                            <StashedDraftItem
                                                key={draft.id}
                                                draft={draft}
                                                onPop={() => editorManager.popStash(draft.id)}
                                                onDelete={() => editorManager.deleteStash(draft.id)}
                                            />
                                        ))}
                                    </div>
                                ) : (
                                    <div className="border border-dashed border-border p-4 text-sm text-muted">
                                        Stashed drafts show up here so you can swap them back into the editor.
                                    </div>
                                )}
                            </div>
                        </Popover.Popup>
                    </Popover.Positioner>
                </Popover.Portal>
            </Popover.Root>
        </div>
    )
})

export const TaskCreatePage = observer(({ workspaceId, repo }: TaskCreatePageProps) => {
    const codeStore = useCodeStore()
    const navigate = useCodeNavigate()
    const portalContainer = usePortalContainer()
    const [selectedMcpServerIds, setSelectedMcpServerIds] = useState<string[]>([])
    const [adminCapabilities, setAdminCapabilities] = useState(false)
    const editorRef = useRef<SmartEditorRef>(null)
    const fileInputRef = useRef<HTMLInputElement>(null)

    // Get SmartEditorManager for task creation
    const editorManager = codeStore.smartEditors.getManager("task-create", workspaceId)
    const workspaceFsContext = useMemo(() => (repo?.path ? { workspaceId, workspaceRoot: repo.path } : null), [repo?.path, workspaceId])

    // Page-level drop zone for images
    const { isDragOver, dragHandlers } = useImageDropZone(editorManager)

    // SDK capabilities for slash command autocomplete
    const sdkCapabilities = useMemo(() => new SdkCapabilitiesManager(), [])
    const agentOptions = getNewTaskAgentOptions(providerModelRegistry, {
        harnessId: codeStore.defaultHarnessId,
        modelId: codeStore.defaultModel,
    }).map((option) => ({ ...option, thinking: codeStore.defaultThinking, id: agentTargetId({ ...option, thinking: codeStore.defaultThinking }) }))
    const pinnedAgentOptions = getNewTaskPinnedAgents({
        options: agentOptions,
        pinned: codeStore.personalSettingsStore?.settings.current.newTaskPinnedAgents,
    })

    // Refocus when the repo/editor identity changes.
    useEffect(() => {
        editorRef.current?.focus()
    }, [repo?.path, editorManager])

    const handleCreate = (agentOption?: NewTaskAgentOption) => {
        const target = agentOption ?? pinnedAgentOptions[0]
        if (!editorManager.value.trim() || !workspaceId || !target) return

        const description = editorManager.value.trim()
        const images = [...editorManager.pendingImages]
        const recoveryDraft = editorManager.stashCurrentDraft()
        if (!recoveryDraft) return

        console.log("[TaskCreatePage] Creating task with MCP servers", {
            selectedMcpServerIds,
            enabledServersCount: codeStore.mcpServers.enabledServers.length,
        })

        codeStore.recordNewTaskAgentLaunch(target)

        const creationId = codeStore.creation.newTask({
            repoId: workspaceId,
            description,
            recoveryDraftId: recoveryDraft.id,
            images,
            enabledMcpServerIds: selectedMcpServerIds.length > 0 ? selectedMcpServerIds : undefined,
            harnessId: target.harnessId,
            modelId: target.modelId,
            thinking: target.thinking ?? codeStore.defaultThinking,
            fastMode: codeStore.defaultFastMode,
            chiefOfStaff: adminCapabilities,
        })

        editorRef.current?.clear()
        setSelectedMcpServerIds([])
        setAdminCapabilities(false)
        navigate.go("CodeWorkspaceTaskCreating", { workspaceId, creationId })
    }

    const handleEditorKeyDown = (event: ReactKeyboardEvent) => {
        if (isSubmitShortcut(event)) {
            event.preventDefault()
            handleCreate()
            return
        }

        if (event.key !== "Escape") return

        event.preventDefault()
        editorRef.current?.blur()
    }

    useHotkeys(
        INPUT_FOCUS_HOTKEYS,
        (event) => {
            event.preventDefault()
            editorRef.current?.focusEnd()
        },
        { enableOnContentEditable: true, enableOnFormTags: ["INPUT", "TEXTAREA", "SELECT"] },
        []
    )

    const isDisabled = !editorManager.hasText
    const creations = codeStore.creation.getCreationsForRepo(workspaceId)
    const pendingCreations = creations.filter((c) => c.completedTaskId === null)
    const handleRetryCreation = (creationId: string) => {
        codeStore.creation.retryCreation(creationId)
    }

    const handleDismissCreation = async (creationId: string) => {
        await codeStore.creation.dismissCreation(creationId)
    }

    const handleEditorAreaClick = () => {
        editorRef.current?.focus()
    }

    return (
        <div className="flex flex-col h-full overflow-hidden relative" {...dragHandlers}>
            {isDragOver && <ImageDropOverlay />}
            <div
                className="relative flex-1 min-h-0 overflow-y-auto p-6 max-[900px]:p-4 cursor-text"
                onClick={handleEditorAreaClick}
                {...MAIN_INPUT_HOTKEY_SCOPE_PROPS}
            >
                <SmartEditor
                    key={`${editorManager.workspaceId}:${editorManager.id}`}
                    ref={editorRef}
                    manager={editorManager}
                    fileMentionsDir={repo?.path ?? null}
                    fileMentionsContext={workspaceFsContext}
                    slashCommandsDir={repo?.path ?? null}
                    sdkCapabilities={sdkCapabilities}
                    onKeyDown={handleEditorKeyDown}
                    placeholder="Describe your task... Use @ to reference files, / for commands"
                    className="h-full text-base border-0 bg-transparent [&>div]:h-full [&>div]:border-0 [&>div]:border-l-2 [&>div]:border-l-transparent [&>div:focus-within]:border-l-primary [&>div]:transition-colors"
                    editorClassName="h-full"
                />
            </div>

            {/* Bottom bar */}
            <div className="flex-shrink-0 border-t border-border bg-base-100">
                <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/gif,image/webp"
                    className="hidden"
                    onChange={(e) => {
                        const file = e.target.files?.[0]
                        if (file) {
                            processImageBlob(file).then(({ attachment, dataUrl }) => {
                                editorManager.addImage(attachment, dataUrl)
                            })
                            e.target.value = ""
                        }
                    }}
                />

                {/* Image preview strip */}
                {editorManager.pendingImages.length > 0 && (
                    <div className="flex gap-2 px-4 py-2 border-b border-border overflow-x-auto">
                        {editorManager.pendingImages.map((img) => (
                            <div key={img.id} className="relative shrink-0 group">
                                <img
                                    src={editorManager.pendingImageDataUrls.get(img.id)}
                                    alt=""
                                    className="h-20 object-cover"
                                    style={{ aspectRatio: `${img.resizedWidth}/${img.resizedHeight}` }}
                                />
                                <button
                                    type="button"
                                    className="btn absolute -top-1.5 -right-1.5 bg-base-300 p-0.5 opacity-0 max-[900px]:opacity-100 group-hover:opacity-100 transition-opacity"
                                    onClick={() => editorManager.removeImage(img.id)}
                                >
                                    <X size={12} />
                                </button>
                            </div>
                        ))}
                    </div>
                )}

                {/* Main toolbar row */}
                <div className="flex flex-wrap items-center gap-4 px-4 py-3 max-[900px]:hidden">
                    {/* Left section: Connectors - scrollable */}
                    <div className="flex-1 min-w-0 overflow-x-auto">
                        <TaskMcpSelector selectedServerIds={selectedMcpServerIds} onSelectionChange={setSelectedMcpServerIds} compact iconOnly />
                    </div>

                    <AdminCapabilitiesSwitch checked={adminCapabilities} onChange={setAdminCapabilities} />

                    {/* Image attach + run options */}
                    <div className="flex shrink-0 items-center gap-1">
                        <button
                            type="button"
                            className="btn p-2 text-muted hover:text-base-content hover:bg-base-200 transition-colors"
                            onClick={() => fileInputRef.current?.click()}
                            title="Attach image"
                        >
                            <ImagePlus size={16} />
                        </button>
                        <ThinkingPicker value={codeStore.defaultThinking} onChange={(t) => codeStore.setDefaultThinking(t)} />
                        <FastModeToggle enabled={codeStore.defaultFastMode} onChange={(enabled) => codeStore.setDefaultFastMode(enabled)} />
                    </div>

                    {/* Right section: Pending + Action */}
                    <div className="flex flex-wrap items-center gap-2 flex-shrink-0">
                        {/* Pending tasks indicator */}
                        {pendingCreations.length > 0 && (
                            <Popover.Root>
                                <Popover.Trigger className="btn flex items-center gap-1.5 px-2 py-1.5 text-xs text-muted hover:text-base-content hover:bg-base-200 transition-colors">
                                    <Loader2 size={12} className="animate-spin" />
                                    <span>{pendingCreations.length} pending</span>
                                    <ChevronDown size={12} />
                                </Popover.Trigger>
                                <Popover.Portal container={portalContainer}>
                                    <Popover.Positioner sideOffset={8} side="top" align="end">
                                        <Popover.Popup className="z-50 w-[min(20rem,calc(100vw-1rem))] bg-base-100 border border-border shadow-lg outline-none">
                                            <div className="px-3 py-2 border-b border-border">
                                                <span className="text-xs font-medium text-muted">Pending Tasks</span>
                                            </div>
                                            <div className="max-h-64 overflow-y-auto">
                                                {pendingCreations.map((creation) => (
                                                    <PendingTaskItem
                                                        key={creation.id}
                                                        creation={creation}
                                                        onClick={() => navigate.go("CodeWorkspaceTaskCreating", { workspaceId, creationId: creation.id })}
                                                        onRetry={() => handleRetryCreation(creation.id)}
                                                        onDismiss={() => handleDismissCreation(creation.id)}
                                                    />
                                                ))}
                                            </div>
                                        </Popover.Popup>
                                    </Popover.Positioner>
                                </Popover.Portal>
                            </Popover.Root>
                        )}

                        <TaskCreateAgentActions
                            options={agentOptions}
                            pinnedOptions={pinnedAgentOptions}
                            disabled={isDisabled}
                            onCreate={handleCreate}
                            onPinnedOptionsChange={(nextPinned) => codeStore.setNewTaskPinnedAgents(nextPinned)}
                        />
                    </div>
                </div>

                <div className="hidden max-[900px]:block">
                    <div className="flex min-w-0 flex-wrap items-center justify-end gap-1.5 border-b border-border px-3 py-2">
                        <div className="mr-auto">
                            <AdminCapabilitiesSwitch checked={adminCapabilities} onChange={setAdminCapabilities} />
                        </div>
                        <div className="shrink-0">
                            <ThinkingPicker value={codeStore.defaultThinking} onChange={(thinking) => codeStore.setDefaultThinking(thinking)} touchFriendly />
                        </div>
                        <FastModeToggle
                            enabled={codeStore.defaultFastMode}
                            onChange={(enabled) => codeStore.setDefaultFastMode(enabled)}
                            touchFriendly
                            showLabel
                        />
                    </div>

                    <details className="group border-b border-border">
                        <summary className="flex min-h-11 cursor-pointer list-none items-center justify-center gap-1.5 px-3 py-2 text-xs text-muted hover:text-base-content [&::-webkit-details-marker]:hidden">
                            <span>Advanced</span>
                            <ChevronDown size={13} className="transition-transform group-open:rotate-180" />
                        </summary>

                        <div className="max-h-[45dvh] overflow-y-auto overscroll-contain border-t border-border px-3 py-3">
                            <div className="mx-auto max-w-2xl space-y-4">
                                <section>
                                    <div className="mb-2 text-xs font-medium text-muted">Context</div>
                                    <div className="flex flex-wrap items-center gap-2">
                                        <button
                                            type="button"
                                            className="btn flex min-h-11 items-center gap-2 border border-border px-3 py-2 text-sm text-base-content hover:bg-base-200"
                                            onClick={() => fileInputRef.current?.click()}
                                        >
                                            <ImagePlus size={16} className="text-muted" />
                                            Image
                                        </button>
                                        <TaskMcpSelector
                                            selectedServerIds={selectedMcpServerIds}
                                            onSelectionChange={setSelectedMcpServerIds}
                                            compact
                                            touchFriendly
                                        />
                                    </div>
                                </section>

                                <section>
                                    <div className="mb-2 text-xs font-medium text-muted">Workflow</div>
                                    <div className="flex flex-wrap items-center gap-2">
                                        <TaskCreateDraftsMenu workspaceId={workspaceId} mobileAdvanced />
                                        {pendingCreations.length > 0 && (
                                            <Popover.Root>
                                                <Popover.Trigger className="btn flex min-h-11 items-center gap-1.5 px-3 py-2 text-xs text-muted hover:bg-base-200 hover:text-base-content">
                                                    <Loader2 size={12} className="animate-spin" />
                                                    <span>{pendingCreations.length} pending</span>
                                                    <ChevronDown size={12} />
                                                </Popover.Trigger>
                                                <Popover.Portal container={portalContainer}>
                                                    <Popover.Positioner sideOffset={8} side="top" align="end">
                                                        <Popover.Popup className="z-50 w-[min(20rem,calc(100vw-1rem))] bg-base-100 border border-border shadow-lg outline-none">
                                                            <div className="px-3 py-2 border-b border-border">
                                                                <span className="text-xs font-medium text-muted">Pending Tasks</span>
                                                            </div>
                                                            <div className="max-h-64 overflow-y-auto">
                                                                {pendingCreations.map((creation) => (
                                                                    <PendingTaskItem
                                                                        key={creation.id}
                                                                        creation={creation}
                                                                        onClick={() =>
                                                                            navigate.go("CodeWorkspaceTaskCreating", {
                                                                                workspaceId,
                                                                                creationId: creation.id,
                                                                            })
                                                                        }
                                                                        onRetry={() => handleRetryCreation(creation.id)}
                                                                        onDismiss={() => handleDismissCreation(creation.id)}
                                                                    />
                                                                ))}
                                                            </div>
                                                        </Popover.Popup>
                                                    </Popover.Positioner>
                                                </Popover.Portal>
                                            </Popover.Root>
                                        )}
                                    </div>
                                </section>
                            </div>
                        </div>
                    </details>

                    <div className="px-3 pt-3" style={{ paddingBottom: "max(0.75rem, env(safe-area-inset-bottom))" }}>
                        <div className="mx-auto max-w-2xl">
                            <TaskCreateAgentActions
                                options={agentOptions}
                                pinnedOptions={pinnedAgentOptions}
                                disabled={isDisabled}
                                mobile
                                onCreate={handleCreate}
                                onPinnedOptionsChange={(nextPinned) => codeStore.setNewTaskPinnedAgents(nextPinned)}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
})
